import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { useApiError } from '@/lib/useApiError';
import { usePillarMix } from '@/queries/phase2';
import { PillarEditForm } from '@/features/phase2/PillarEditForm';
import {
  PILLAR_MIX_WINDOWS,
  type PillarMixRow,
  type PillarMixWindow,
} from '@/schemas/admin/phase2/pillars';
import { useUiStore } from '@/stores/ui';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/phase2/pillars')({
  component: PillarsScreen,
});

function PillarsScreen() {
  const { t } = useTranslation('phase2');
  const [windowSel, setWindowSel] = React.useState<PillarMixWindow>('7d');
  const mix = usePillarMix(windowSel);
  const resolveError = useApiError();
  const [editing, setEditing] = React.useState<PillarMixRow | null>(null);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('pillars.title', { defaultValue: 'Pillars' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('pillars.subtitle', {
              defaultValue:
                'Editorial mix dial. Targets are per-pillar weekly percentages; actuals are computed over the chosen window.',
            })}
          </p>
        </div>
      </header>

      <Tabs value={windowSel} onValueChange={(v) => setWindowSel(v as PillarMixWindow)}>
        <TabsList>
          {PILLAR_MIX_WINDOWS.map((w) => (
            <TabsTrigger key={w} value={w}>
              {t(`pillars.window.${w}`, { defaultValue: w })}
            </TabsTrigger>
          ))}
        </TabsList>
        {PILLAR_MIX_WINDOWS.map((w) => (
          <TabsContent key={w} value={w}>
            {mix.error ? (
              <Card>
                <CardContent className="p-6 text-sm text-[color:var(--color-danger)]">
                  {resolveError(mix.error).message}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {(mix.data?.pillars ?? []).map((pillar) => (
                  <PillarCard
                    key={pillar.pillar_key}
                    pillar={pillar}
                    onEdit={() => setEditing(pillar)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('pillars.editTitle', {
                defaultValue: 'Edit pillar: {key}',
                key: editing?.pillar_key ?? '',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('pillars.editBody', {
                defaultValue:
                  'Pillar key is immutable to keep tagged content linked. Edit names, submessages, target, accent, position, or active flag.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <PillarEditForm
              pillar={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PillarCard({ pillar, onEdit }: { pillar: PillarMixRow; onEdit: () => void }) {
  const { t } = useTranslation('phase2');
  const locale = useUiStore((s) => s.locale);
  const name =
    (locale === 'eg-ar' ? pillar.name_eg_ar : locale === 'ar' ? pillar.name_ar : pillar.name_en) ??
    pillar.name_en ??
    pillar.pillar_key;
  const sub =
    (locale === 'eg-ar'
      ? pillar.submessage_eg_ar
      : locale === 'ar'
        ? pillar.submessage_ar
        : pillar.submessage_en) ?? '';

  const target = pillar.target_weekly_pct;
  const actual = pillar.actual_pct ?? 0;
  const gap = actual - target;
  const tone = Math.abs(gap) <= 3 ? 'success' : gap > 0 ? 'info' : 'warn';

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs font-mono uppercase tracking-wide text-[color:var(--color-fg-muted)]">
              {pillar.pillar_key}
            </p>
            <h2 className="text-lg font-semibold">{name}</h2>
            {sub ? (
              <p className="text-sm text-[color:var(--color-fg-muted)]">{sub}</p>
            ) : null}
          </div>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Pencil className="size-4" aria-hidden />
            <span className="sr-only md:not-sr-only md:text-xs">
              {t('pillars.edit', { defaultValue: 'Edit' })}
            </span>
          </Button>
        </div>

        <div className="flex items-baseline justify-between text-sm">
          <span className="text-[color:var(--color-fg-muted)]">
            {t('pillars.target', { defaultValue: 'Target' })}
          </span>
          <span dir="ltr" className="font-mono">
            {target}%
          </span>
        </div>
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-[color:var(--color-fg-muted)]">
            {t('pillars.actual', { defaultValue: 'Actual' })}
          </span>
          <span dir="ltr" className="font-mono">
            {actual.toFixed(1)}%
          </span>
        </div>
        <Bar target={target} actual={actual} />
        <Badge tone={tone}>
          {gap >= 0
            ? t('pillars.over', { defaultValue: '+{n}% vs target', n: gap.toFixed(1) })
            : t('pillars.under', { defaultValue: '{n}% vs target', n: gap.toFixed(1) })}
        </Badge>

        <div className="grid grid-cols-4 gap-2 text-xs">
          {(['stories', 'posts', 'events', 'groups'] as const).map((kind) => (
            <div key={kind} className="rounded-[var(--radius-sm)] bg-[color:var(--color-surface-2)] p-2">
              <p className="text-[color:var(--color-fg-muted)]">
                {t(`pillars.subjects.${kind}`, { defaultValue: kind })}
              </p>
              <p dir="ltr" className="font-mono text-base font-semibold">
                {pillar[kind] ?? 0}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Bar({ target, actual }: { target: number; actual: number }) {
  const max = Math.max(target, actual, 1);
  const tw = (target / max) * 100;
  const aw = (actual / max) * 100;
  return (
    <div className="space-y-1">
      <div className="relative h-3 overflow-hidden rounded-[var(--radius-pill)] bg-[color:var(--color-surface-2)]">
        <div
          className={cn('absolute inset-y-0 start-0 bg-[color:var(--color-accent-soft)]')}
          style={{ width: `${tw}%` }}
          aria-label="target"
        />
        <div
          className={cn('absolute inset-y-0 start-0 bg-[color:var(--color-accent)]')}
          style={{ width: `${aw}%` }}
          aria-label="actual"
        />
      </div>
    </div>
  );
}
